import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, Menu, X, LayoutDashboard, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, waLink } from "@/lib/config";

const NAV_LINKS = [
  { index: "01", label: "Brands", href: "/#brands", testid: "nav-link-brands" },
  { index: "02", label: "Estimators", href: "/calculators", testid: "nav-link-estimators" },
  { index: "03", label: "Inventory App", href: "/dashboard", testid: "nav-link-inventory" },
  { index: "04", label: "Logistics Network", href: "/#logistics", testid: "nav-link-logistics" },
  { index: "05", label: "Why Us", href: "/#manifesto", testid: "nav-link-manifesto" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#0B0D11]/90 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-brand-logo">
          <img src={LOGO_URL} alt="Infra Edge logo" className="h-10 w-10 rounded-full object-cover border border-white/10" />
          <div className="leading-none">
            <span className="font-heading font-extrabold text-lg tracking-wide text-white uppercase">Infra Edge</span>
            <span className="block font-mono text-[10px] tracking-[0.3em] text-brand uppercase">Cement Solutions</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.testid}
              href={l.href}
              data-testid={l.testid}
              className="group font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-white transition-colors"
            >
              <span className="text-brand mr-1.5">{l.index}.</span>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={waLink("Hello Infra Edge, I need a bulk cement supply quote for my site.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-btn-whatsapp"
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-ink font-heading font-bold text-sm uppercase tracking-wide px-4 py-2 transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp Quote
          </a>
          <button
            onClick={() => navigate(user ? "/dashboard" : "/login")}
            data-testid="nav-btn-auth-toggle"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-brand/50 text-gray-200 hover:text-brand font-heading font-bold text-sm uppercase tracking-wide px-4 py-2 transition-colors"
          >
            {user ? <LayoutDashboard className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
            {user ? "Dashboard" : "Contractor Login"}
          </button>
        </div>

        <button
          className="lg:hidden text-gray-200"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-ink px-4 py-4 space-y-3">
          {NAV_LINKS.map((l) => (
            <a
              key={l.testid}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block font-mono text-sm uppercase tracking-widest text-gray-300 py-1"
            >
              <span className="text-brand mr-2">{l.index}.</span>
              {l.label}
            </a>
          ))}
          <div className="flex gap-3 pt-2">
            <a
              href={waLink("Hello Infra Edge, I need a bulk cement supply quote.")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center bg-brand text-ink font-heading font-bold uppercase text-sm px-4 py-2.5"
            >
              WhatsApp Quote
            </a>
            <button
              onClick={() => {
                setOpen(false);
                navigate(user ? "/dashboard" : "/login");
              }}
              className="flex-1 border border-white/15 text-gray-200 font-heading font-bold uppercase text-sm px-4 py-2.5"
            >
              {user ? "Dashboard" : "Login"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
